import { msg } from "@lingui/core/macro";
import { ChevronLeft, ChevronRight } from "@fleet/icons";
import { createEffect, createMemo, createSignal, For, onCleanup, Show, untrack } from "solid-js";
import { cn, getMonthMatrix, isSameLocalDay, toLocalDateKey } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { CALENDAR_SWIPE } from "@/lib/gestures/presets";

const DAYS_IN_WEEK = 7;
const REFERENCE_YEAR = 2024;
const WEEKDAY_REFERENCE_MONDAY = new Date(REFERENCE_YEAR, 0, 1); // a known Monday

interface CalendarProps {
  /** Currently selected day (local), or null. */
  selected: () => Date | null;
  onSelect: (date: Date) => void;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

/** Accessible month calendar with full keyboard navigation (ARIA grid). */
export function Calendar(props: CalendarProps) {
  const { t, locale } = useI18n();
  const today = startOfDay(new Date());
  // One-time snapshot to seed the visible month / focus; the panel remounts when
  // reopened, so it never needs to react to later `selected` changes here.
  const initial = untrack(() => props.selected()) ?? today;
  const [view, setView] = createSignal({ year: initial.getFullYear(), month: initial.getMonth() });
  const [focused, setFocused] = createSignal(startOfDay(initial));

  // Header drill-down: tap the month → choose a month; tap the year → choose a year
  // (standard date-picker behavior). Month swiping remains active
  // only in "days" mode.
  const [mode, setMode] = createSignal<"days" | "months" | "years">("days");
  const YEARS_PER_PAGE = 12;
  const [yearPageStart, setYearPageStart] = createSignal(
    Math.floor(initial.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE,
  );

  const monthNames = createMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale(), { month: "short" });
    return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(REFERENCE_YEAR, m, 1)));
  });
  const headerMonthName = createMemo(() =>
    new Intl.DateTimeFormat(locale(), { month: "long" }).format(
      new Date(view().year, view().month, 1),
    ),
  );
  const yearPageEnd = () => yearPageStart() + YEARS_PER_PAGE - 1;

  const openMonths = () => {
    setMode((m) => (m === "months" ? "days" : "months"));
  };
  const openYears = () => {
    setYearPageStart(Math.floor(view().year / YEARS_PER_PAGE) * YEARS_PER_PAGE);
    setMode((m) => (m === "years" ? "days" : "years"));
  };
  const pickMonth = (month: number) => {
    setView((current) => ({ ...current, month }));
    vibrate(CALENDAR_SWIPE.hapticMs);
    setMode("days");
  };
  const pickYear = (year: number) => {
    setView((current) => ({ ...current, year }));
    vibrate(CALENDAR_SWIPE.hapticMs);
    setMode("days");
  };

  createEffect(() => {
    const sel = props.selected();
    if (sel) {
      const normalized = startOfDay(sel);
      setView({ year: normalized.getFullYear(), month: normalized.getMonth() });
      setFocused(normalized);
    }
  });
  const weeks = createMemo(() => getMonthMatrix(view().year, view().month));

  const weekdayLabels = createMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale(), { weekday: "short" });
    return Array.from({ length: DAYS_IN_WEEK }, (_, index) =>
      fmt.format(addDays(WEEKDAY_REFERENCE_MONDAY, index)),
    );
  });

  const monthLabel = createMemo(() =>
    new Intl.DateTimeFormat(locale(), { month: "long", year: "numeric" }).format(
      new Date(view().year, view().month, 1),
    ),
  );

  const fullDateLabel = (date: Date) =>
    new Intl.DateTimeFormat(locale(), { dateStyle: "full" }).format(date);

  const shiftMonth = (delta: number) => {
    setView((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  // Advances one "page" according to the given mode: days→month, months→year, years→decade.
  const commitPage = (delta: number, pageMode: "days" | "months" | "years") => {
    if (pageMode === "days") {
      shiftMonth(delta);
    } else if (pageMode === "months") {
      setView((current) => ({ ...current, year: current.year + delta }));
    } else {
      setYearPageStart((start) => start + delta * YEARS_PER_PAGE);
    }
  };

  const [gridRef, setGridRef] = createSignal<HTMLDivElement>();

  // --- Horizontal page swipe (carousel) -----------------------------------
  // The grid follows the finger on X; on commit (or with a fling), the current
  // page exits and the new one enters from the opposite side. It works in all
  // THREE modes (days/months/years): same gesture and slide. Physics
  // (axis-lock, deadzone, fling) is shared; tuning is in CALENDAR_SWIPE.
  const [dragX, setDragX] = createSignal(0);
  const [transitioning, setTransitioning] = createSignal(false);
  let swiping = false; // true once a horizontal swipe engages (suppresses the tap)

  const gridWidth = () => gridRef()?.clientWidth ?? CALENDAR_SWIPE.fallbackWidthPx;

  /** Animates a directional page change. delta>0 = next (exits left). */
  const animatePage = (delta: number) => {
    if (delta === 0) {
      return;
    }
    // Capture the mode HERE (gesture/click) for the deferred commit, avoiding
    // a signal read inside setTimeout (a Solid reactivity false positive).
    const pageMode = mode();
    if (prefersReducedMotion()) {
      commitPage(delta, pageMode);
      setDragX(0);
      return;
    }
    const width = gridWidth();
    const outX = delta > 0 ? -width : width; // the current page exits in the direction of travel
    setTransitioning(true);
    setDragX(outX);
    vibrate(CALENDAR_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      commitPage(delta, pageMode);
      // Jump (without transition) to the opposite edge, then slide in the new page.
      setTransitioning(false);
      setDragX(-outX);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitioning(true);
          setDragX(0);
        });
      });
    }, CALENDAR_SWIPE.transitionMs);
  };

  const snapBack = () => {
    setTransitioning(true);
    setDragX(0);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: CALENDAR_SWIPE.thresholdPx,
    fling: { ...CALENDAR_SWIPE.fling },
    onStart: () => {
      swiping = true;
      setTransitioning(false);
    },
    onMove: (sample) => {
      setTransitioning(false);
      setDragX(sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        CALENDAR_SWIPE.commitThresholdPx,
        gridWidth() * CALENDAR_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next page; swipe right (dx>0) → previous page.
        animatePage(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
      // Clear tap-suppression after the click would have fired.
      queueMicrotask(() => {
        swiping = false;
      });
    },
    onCancel: () => {
      snapBack();
      swiping = false;
    },
  });

  const swipeHandlers = pointerHandlers(recognizer);

  const moveFocus = (date: Date) => {
    const normalized = startOfDay(date);
    setFocused(normalized);
    if (normalized.getMonth() !== view().month || normalized.getFullYear() !== view().year) {
      setView({ year: normalized.getFullYear(), month: normalized.getMonth() });
    }
    // Focus the matching cell after the grid re-renders. The grid element is
    // stable, so capture it now and query it once the microtask runs.
    const grid = gridRef();
    queueMicrotask(() => {
      const key = toLocalDateKey(normalized);
      grid?.querySelector<HTMLButtonElement>(`[data-day="${key}"]`)?.focus();
    });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const handlers: Record<string, () => void> = {
      ArrowLeft: () => {
        moveFocus(addDays(focused(), -1));
      },
      ArrowRight: () => {
        moveFocus(addDays(focused(), 1));
      },
      ArrowUp: () => {
        moveFocus(addDays(focused(), -DAYS_IN_WEEK));
      },
      ArrowDown: () => {
        moveFocus(addDays(focused(), DAYS_IN_WEEK));
      },
      PageUp: () => {
        moveFocus(new Date(focused().getFullYear(), focused().getMonth() - 1, focused().getDate()));
      },
      PageDown: () => {
        moveFocus(new Date(focused().getFullYear(), focused().getMonth() + 1, focused().getDate()));
      },
      Enter: () => {
        props.onSelect(focused());
      },
      " ": () => {
        props.onSelect(focused());
      },
    };
    const handler = handlers[event.key];
    if (handler) {
      event.preventDefault();
      handler();
    }
  };

  onCleanup(() => {
    recognizer.cancel();
  });

  // Header chevrons advance one page (with the same slide as the swipe).
  const headerStep = (delta: number) => {
    animatePage(delta);
  };

  const gridTransform = () => `translate3d(${dragX()}px, 0, 0)`;
  const gridTransition = () =>
    transitioning() ? `transform ${CALENDAR_SWIPE.transitionMs}ms ease-out` : "none";

  return (
    <div class="select-none">
      <div class="flex items-center justify-between px-1 pb-2">
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={
            mode() === "years"
              ? t(msg`Previous years`)
              : mode() === "months"
                ? t(msg`Previous year`)
                : t(msg`Previous month`)
          }
          onClick={() => {
            headerStep(-1);
          }}
        >
          <ChevronLeft class="h-4 w-4" />
        </button>

        <Show
          when={mode() === "days"}
          fallback={
            <span class="text-sm font-semibold capitalize" aria-live="polite">
              <Show when={mode() === "months"} fallback={`${yearPageStart()} – ${yearPageEnd()}`}>
                <button
                  type="button"
                  class="rounded-md px-2 py-0.5 transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  onClick={openYears}
                >
                  {view().year}
                </button>
              </Show>
            </span>
          }
        >
          <span class="flex items-center gap-1 text-sm font-semibold" aria-live="polite">
            <button
              type="button"
              class="rounded-md px-2 py-0.5 capitalize transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={t(msg`Select month`)}
              onClick={openMonths}
            >
              {headerMonthName()}
            </button>
            <button
              type="button"
              class="rounded-md px-2 py-0.5 transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={t(msg`Select year`)}
              onClick={openYears}
            >
              {view().year}
            </button>
          </span>
        </Show>

        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={
            mode() === "years"
              ? t(msg`Next years`)
              : mode() === "months"
                ? t(msg`Next year`)
                : t(msg`Next month`)
          }
          onClick={() => {
            headerStep(1);
          }}
        >
          <ChevronRight class="h-4 w-4" />
        </button>
      </div>

      {/* Etiquetas de día de la semana: fijas (no deslizan), solo en modo días. */}
      <Show when={mode() === "days"}>
        <div class="grid grid-cols-7 gap-0.5 px-1 pb-1">
          <For each={weekdayLabels()}>
            {(label) => (
              <span class="flex h-7 items-center justify-center text-[11px] font-medium uppercase text-muted-foreground">
                {label}
              </span>
            )}
          </For>
        </div>
      </Show>

      {/* Superficie de swipe común a los 3 modos: recorta la página deslizante y
          posee el gesto horizontal. `touch-action: pan-y` conserva el scroll
          vertical nativo mientras reclamamos el eje horizontal. El contenedor con
          `ref`/transform es estable (siempre montado) → el ancho y el deslizamiento
          funcionan igual en días, meses y años. */}
      <div class="overflow-hidden" style={{ "touch-action": "pan-y" }} {...swipeHandlers}>
        <div
          ref={setGridRef}
          class="will-change-transform"
          style={{ transform: gridTransform(), transition: gridTransition() }}
        >
          {/* Días */}
          <Show when={mode() === "days"}>
            {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
            <div
              role="grid"
              aria-label={monthLabel()}
              class="grid grid-cols-7 gap-0.5 px-1"
              onKeyDown={onKeyDown}
            >
              <For each={weeks()}>
                {(week) => (
                  <For each={week}>
                    {(day) => {
                      const isCurrentMonth = () => day.getMonth() === view().month;
                      const isSelected = () => {
                        const sel = props.selected();
                        return sel ? isSameLocalDay(sel, day) : false;
                      };
                      const isToday = () => isSameLocalDay(today, day);
                      const isFocusTarget = () => isSameLocalDay(focused(), day);
                      return (
                        <button
                          type="button"
                          role="gridcell"
                          data-day={toLocalDateKey(day)}
                          tabindex={isFocusTarget() ? 0 : -1}
                          aria-selected={isSelected()}
                          aria-label={fullDateLabel(day)}
                          class={cn(
                            "relative flex h-9 w-9 items-center justify-center rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            isCurrentMonth() ? "text-foreground" : "text-muted-foreground/40",
                            isSelected()
                              ? "bg-primary text-primary-foreground font-semibold hover:bg-primary"
                              : "hover:bg-muted/20",
                          )}
                          onClick={() => {
                            // A horizontal swipe captures the pointer and must not
                            // also register as a day tap.
                            if (swiping) {
                              return;
                            }
                            setFocused(startOfDay(day));
                            props.onSelect(day);
                          }}
                        >
                          {day.getDate()}
                          <Show when={isToday() && !isSelected()}>
                            <span class="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-primary" />
                          </Show>
                        </button>
                      );
                    }}
                  </For>
                )}
              </For>
            </div>
          </Show>

          {/* Selector de MES (swipe ‹›: ±1 año) */}
          <Show when={mode() === "months"}>
            <div class="grid grid-cols-3 gap-1.5 px-1 pb-1">
              <For each={monthNames()}>
                {(label, index) => {
                  const isCurrent = () => index() === view().month;
                  return (
                    <button
                      type="button"
                      class={cn(
                        "flex h-10 items-center justify-center rounded-lg text-sm capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        isCurrent()
                          ? "bg-primary text-primary-foreground font-semibold"
                          : "text-foreground hover:bg-muted/20",
                      )}
                      onClick={() => {
                        if (swiping) {
                          return;
                        }
                        pickMonth(index());
                      }}
                    >
                      {label}
                    </button>
                  );
                }}
              </For>
            </div>
          </Show>

          {/* Selector de AÑO (swipe ‹›: ±12 años) */}
          <Show when={mode() === "years"}>
            <div class="grid grid-cols-3 gap-1.5 px-1 pb-1">
              <For
                each={Array.from(
                  { length: YEARS_PER_PAGE },
                  (_, offset) => yearPageStart() + offset,
                )}
              >
                {(year) => {
                  const isCurrent = () => year === view().year;
                  return (
                    <button
                      type="button"
                      class={cn(
                        "flex h-10 items-center justify-center rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        isCurrent()
                          ? "bg-primary text-primary-foreground font-semibold"
                          : "text-foreground hover:bg-muted/20",
                      )}
                      onClick={() => {
                        if (swiping) {
                          return;
                        }
                        pickYear(year);
                      }}
                    >
                      {year}
                    </button>
                  );
                }}
              </For>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}
